import { ClearRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import ExcelJS from "exceljs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { statusOptions } from "data/globalData";
import axios from "axios";
import { useMemo, useState } from "react";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "_");

const parseCsvLine = (line) => {
	const values = [];
	let current = "";
	let inQuotes = false;

	for (let index = 0; index < line.length; index += 1) {
		const char = line[index];
		const nextChar = line[index + 1];

		if (char === '"') {
			if (inQuotes && nextChar === '"') {
				current += '"';
				index += 1;
			} else {
				inQuotes = !inQuotes;
			}
			continue;
		}

		if (char === "," && !inQuotes) {
			values.push(current.trim());
			current = "";
			continue;
		}

		current += char;
	}

	values.push(current.trim());
	return values;
};

const formatCellValue = (value) => {
	if (value == null) return "";
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return value.toISOString();
	}
	if (typeof value === "object") {
		if (value?.text) return String(value.text).trim();
		if (value?.result != null) return String(value.result).trim();
	}
	return String(value).trim();
};

const statusNameMap = Object.values(statusOptions).reduce((acc, option) => {
	acc[option.key] = option.name;
	return acc;
}, {});

const getStatusLabel = (statusKey) => statusNameMap[statusKey] || statusKey || "";

const getShootAssociation = (row = {}) => {
	if (row?.shoot_id) return row.shoot_id;
	if (row?.current_shoot_id) return row.current_shoot_id;
	if (row?.related_shoot_id) return row.related_shoot_id;
	if (Array.isArray(row?.shoot_ids) && row.shoot_ids.length) return row.shoot_ids[0];
	if (Array.isArray(row?.shootSkus) && row.shootSkus.length) return row.shootSkus[0]?.shoot_id || true;
	if (Array.isArray(row?.shoot_skus) && row.shoot_skus.length) return row.shoot_skus[0]?.shoot_id || true;
	if (Number(row?.shoot_count) > 0) return true;
	return null;
};

const buildRowsFromColumn = (sourceRows, columnIndex) =>
	sourceRows.map((row, index) => ({
		__rowId: `upload-row-${index}`,
		row_no: index + 2,
		i_code: formatCellValue(row?.[columnIndex]),
	}));

const UploadIcodesModal = ({ modal, setModal, selectedSkus = [], onAddIcodelist }) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [fileName, setFileName] = useState("");
	const [headers, setHeaders] = useState([]);
	const [sourceRows, setSourceRows] = useState([]);
	const [icodeColumnIndex, setIcodeColumnIndex] = useState("");
	const [previewRows, setPreviewRows] = useState([]);

	const selectedSkuSet = useMemo(() => new Set(selectedSkus), [selectedSkus]);

	const resetState = () => {
		setLoading(false);
		setError("");
		setFileName("");
		setHeaders([]);
		setSourceRows([]);
		setIcodeColumnIndex("");
		setPreviewRows([]);
	};

	const handleClose = () => {
		resetState();
		setModal(false);
	};

	const parseCsvFile = async (file) => {
		const text = await file.text();
		const lines = text.split(/\r?\n/).filter((line) => line.trim());
		if (!lines.length) return { headers: [], rows: [] };

		const parsedHeaders = parseCsvLine(lines[0]).map((value, index) => value || `Column ${index + 1}`);
		const parsedRows = lines.slice(1).map((line) => parseCsvLine(line));

		return { headers: parsedHeaders, rows: parsedRows };
	};

	const parseExcelFile = async (file) => {
		const workbook = new ExcelJS.Workbook();
		const buffer = await file.arrayBuffer();
		await workbook.xlsx.load(buffer);

		const worksheet = workbook.worksheets[0];
		if (!worksheet) return { headers: [], rows: [] };

		const parsedHeaders = worksheet
			.getRow(1)
			.values.slice(1)
			.map((value, index) => formatCellValue(value) || `Column ${index + 1}`);

		const parsedRows = [];
		worksheet.eachRow((row, rowNumber) => {
			if (rowNumber === 1) return;
			parsedRows.push(row.values.slice(1));
		});

		return { headers: parsedHeaders, rows: parsedRows };
	};

	const validateIcodes = async (rows) => {
		const duplicateCounter = new Map();
		rows.forEach((row) => {
			const icode = row.i_code;
			if (!icode) return;
			duplicateCounter.set(icode, (duplicateCounter.get(icode) || 0) + 1);
		});

		const uniqueIcodes = [...new Set(rows.map((row) => row.i_code).filter(Boolean))];

		let apiRows = [];
		if (uniqueIcodes.length) {
			const response = await axios.post(
				"/api/studio/skus_combined_data",
				{ filter: { i_code: uniqueIcodes } },
				{ params: { limit: uniqueIcodes.length, offset: 0 } },
			);
			apiRows = response?.data?.result?.rows || [];
		}

		const rowsByIcode = new Map(apiRows.map((row) => [row.i_code, row]));

		return rows.map((row) => {
			if (!row.i_code) {
				return {
					...row,
					__isValid: false,
					__status: "Missing i_code",
					__note: "Selected column is empty for this row",
					__currentRow: null,
				};
			}

			if ((duplicateCounter.get(row.i_code) || 0) > 1) {
				return {
					...row,
					__isValid: false,
					__status: "Duplicate",
					__note: "Duplicate i_code in uploaded file",
					__currentRow: null,
				};
			}

			const currentRow = rowsByIcode.get(row.i_code);
			if (!currentRow) {
				return {
					...row,
					__isValid: false,
					__status: "Not found",
					__note: "i_code not found",
					__currentRow: null,
				};
			}

			if (selectedSkuSet.has(row.i_code)) {
				return {
					...row,
					__isValid: false,
					__status: "Already selected",
					__note: "i_code is already selected in this shoot",
					__currentRow: currentRow,
				};
			}

			const linkedShootId = getShootAssociation(currentRow);
			if (linkedShootId) {
				return {
					...row,
					__isValid: false,
					__status: "Shoot already created",
					__note: linkedShootId === true ? "Shoot already created for this i_code" : `Linked to shoot ${linkedShootId}`,
					__currentRow: currentRow,
				};
			}

			if (currentRow.status !== statusOptions.RECEIVED.key) {
				return {
					...row,
					__isValid: false,
					__status: "Not received",
					__note: `Current status: ${getStatusLabel(currentRow.status) || "Unknown"}`,
					__currentRow: currentRow,
				};
			}

			return {
				...row,
				__isValid: true,
				__status: "Received",
				__note: "Ready to add",
				__currentRow: currentRow,
			};
		});
	};

	const handleFileChange = async (event) => {
		const selectedFile = event.target.files?.[0];
		event.target.value = "";

		if (!selectedFile) return;

		setLoading(true);
		setError("");

		try {
			const isCsv = selectedFile.name.toLowerCase().endsWith(".csv");
			const parsedData = isCsv ? await parseCsvFile(selectedFile) : await parseExcelFile(selectedFile);

			setFileName(selectedFile.name);
			setHeaders(parsedData.headers);
			setSourceRows(parsedData.rows);
			setPreviewRows([]);

			const defaultIndex = parsedData.headers.findIndex((header) => normalizeHeader(header) === "i_code");
			setIcodeColumnIndex(defaultIndex >= 0 ? String(defaultIndex) : "");
		} catch (uploadError) {
			resetState();
			setError(uploadError.message || "Unable to read the selected file.");
		} finally {
			setLoading(false);
		}
	};

	const handlePreview = async () => {
		if (icodeColumnIndex === "") {
			setError("Select the i_code column first.");
			return;
		}

		setLoading(true);
		setError("");

		try {
			const rows = buildRowsFromColumn(sourceRows, Number(icodeColumnIndex));
			const nextPreviewRows = await validateIcodes(rows);
			setPreviewRows(nextPreviewRows);
		} catch (previewError) {
			setError(parseErrorMessage(previewError));
		} finally {
			setLoading(false);
		}
	};

	const handleAdd = async () => {
		const validIcodes = [...new Set(previewRows.filter((row) => row.__isValid).map((row) => row.i_code))];

		if (!validIcodes.length) {
			setError("No valid i_codes available to add.");
			return;
		}

		const shouldClose = await onAddIcodelist?.(validIcodes, previewRows);
		if (shouldClose !== false) {
			handleClose();
		}
	};

	const validCount = previewRows.filter((row) => row.__isValid).length;

	const previewColumns = useMemo(
		() => [
			{ key: "row_no", name: "ROW", minWidth: 80, maxWidth: 90, headerAlign: "center", align: "center" },
			{ key: "i_code", name: "I_CODE", minWidth: 160, headerAlign: "center", align: "center" },
			{ key: "__status", name: "STATUS", minWidth: 180, headerAlign: "center", align: "center" },
			{ key: "__note", name: "NOTE", minWidth: 260, headerAlign: "center", align: "center" },
		],
		[],
	);

	return (
		<Dialog maxWidth="lg" fullWidth scroll="paper" open={modal} onClose={handleClose} PaperProps={{ sx: { height: "82vh" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6">Upload i_codes</Typography>
				<IconButton onClick={handleClose}>
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
				<Stack spacing={2} sx={{ minHeight: 0, flex: 1 }}>
					<Alert severity="info">
						Upload CSV or Excel, choose the column that contains <strong>i_code</strong>, then preview statuses from the
						available studio APIs before adding valid rows.
					</Alert>

					<Box>
						<Stack direction="row" spacing={1} flexWrap="wrap">
							<Button
								component="label"
								variant="outlined"
								startIcon={loading ? <CircularProgress size={16} /> : <UploadRounded />}>
								Choose CSV / Excel
								<input hidden type="file" accept=".csv,.xlsx" onChange={handleFileChange} disabled={loading} />
							</Button>
							<Button
								variant="outlined"
								startIcon={loading ? <CircularProgress size={16} /> : <RefreshRounded />}
								onClick={handlePreview}
								disabled={loading || !headers.length || icodeColumnIndex === ""}>
								Preview Status
							</Button>
						</Stack>
						{fileName && (
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								{`${fileName} - ${sourceRows.length} rows found`}
							</Typography>
						)}
					</Box>

					{headers.length > 0 && (
						<FormControl size="small" sx={{ maxWidth: 320 }}>
							<InputLabel id="upload-icodes-column-label">i_code Column</InputLabel>
							<Select
								labelId="upload-icodes-column-label"
								label="i_code Column"
								value={icodeColumnIndex}
								onChange={(event) => setIcodeColumnIndex(event.target.value)}>
								{headers.map((header, index) => (
									<MenuItem key={`${header}-${index}`} value={String(index)}>
										{header}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}

					{error && <Alert severity="error">{error}</Alert>}

					{previewRows.length > 0 && (
						<>
							<Box display="flex" justifyContent="space-between" alignItems="center">
								<Typography variant="body2" color="text.secondary">
									{`${validCount} of ${previewRows.length} i_codes can be added`}
								</Typography>
							</Box>
							<Box sx={{ flex: 1, minHeight: 260 }}>
								<TanstackDatagrid
									columns={previewColumns}
									pageData={previewRows}
									setPageData={setPreviewRows}
									getRowId={(row) => row.__rowId}
									disablePagination={true}
									serverSidePagination={false}
									enableHorizontalScroll={true}
									headerHeight={40}
									rowHeight={42}
								/>
							</Box>
						</>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleAdd} disabled={loading || validCount === 0}>
					Add Valid i_codes
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadIcodesModal;
